import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle, Copy, ExternalLink, KeyRound, Loader2, RefreshCw, Store, Unplug, AlertTriangle } from 'lucide-react';
import { marketplaceIntegrationService, MarketplaceConnection } from '../../services/marketplaceIntegrationService';

// Nomes simples dos avisos que a loja manda
export const MARKETPLACE_EVENT_LABELS: Record<string, string> = {
  'order.created': 'Compra nova',
  'order.processing': 'Pedido em separação',
  'order.shipped': 'Pedido enviado',
  'order.delivered': 'Pedido entregue',
  'order.cancelled': 'Pedido cancelado',
  'order.refunded': 'Pedido reembolsado',
  'cart.abandoned': 'Carrinho abandonado',
  'payment.confirmed': 'Pagamento aprovado',
  'payment.failed': 'Pagamento recusado',
  'payment.refunded': 'Pagamento estornado',
  test: 'Teste',
};

const MARKETPLACE_PANEL_URL = 'https://admin-marketplace.devskin.com/settings';

const errMsg = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleString('pt-BR') : '');

interface Props {
  /** Loja escolhida para este fluxo (id da conexão). */
  selectedId?: string | null;
  onSelect: (conn: MarketplaceConnection | null) => void;
  /** Avisa a lista atual (para a página saber se há loja conectada). */
  onChange?: (list: MarketplaceConnection[]) => void;
}

const StatusPill: React.FC<{ conn: MarketplaceConnection }> = ({ conn }) => {
  if (conn.status === 'connected') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/30 dark:text-green-300">
        <CheckCircle className="h-3.5 w-3.5" /> Conectado
      </span>
    );
  }
  if (conn.status === 'waiting_secret') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
        <AlertTriangle className="h-3.5 w-3.5" /> Falta 1 passo
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/30 dark:text-red-300">
      <AlertTriangle className="h-3.5 w-3.5" /> Com problema
    </span>
  );
};

const MarketplaceConnectionPanel: React.FC<Props> = ({ selectedId, onSelect, onChange }) => {
  const [list, setList] = useState<MarketplaceConnection[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [showNewKey, setShowNewKey] = useState(false);
  const [secretFor, setSecretFor] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const apply = useCallback((rows: MarketplaceConnection[]) => {
    setList(rows);
    onChange?.(rows);
  }, [onChange]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await marketplaceIntegrationService.list();
      apply(r.data || []);
      setEvents(r.events || []);
    } catch (e) {
      toast.error(errMsg(e, 'Não consegui carregar as lojas conectadas'));
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { load(); }, [load]);

  // Uma loja só: ela é a deste fluxo
  useEffect(() => {
    if (!loading && list.length === 1 && selectedId !== list[0].id) onSelect(list[0]);
    if (!loading && selectedId && list.length && !list.find((c) => c.id === selectedId)) onSelect(list.length === 1 ? list[0] : null);
  }, [loading, list, selectedId, onSelect]);

  const handleConnect = async () => {
    const key = apiKey.trim();
    if (!key.startsWith('dsk_')) {
      toast.error('A chave começa com "dsk_". Copie de novo no marketplace.');
      return;
    }
    try {
      setConnecting(true);
      const r = await marketplaceIntegrationService.connect(key);
      setApiKey('');
      setShowNewKey(false);
      if (r.webhook.registered) toast.success(r.message);
      else toast(r.message, { icon: '⚠️' });
      const fresh = await marketplaceIntegrationService.list();
      apply(fresh.data || []);
      setEvents(fresh.events || []);
      onSelect(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Não consegui conectar a loja'));
    } finally {
      setConnecting(false);
    }
  };

  const handleSecret = async (conn: MarketplaceConnection) => {
    try {
      setBusy(conn.id);
      const r = await marketplaceIntegrationService.saveSecret(conn.id, (secretFor[conn.id] || '').trim());
      toast.success(r.message);
      setSecretFor((p) => ({ ...p, [conn.id]: '' }));
      apply(list.map((c) => (c.id === conn.id ? r.data : c)));
    } catch (e) {
      toast.error(errMsg(e, 'Não consegui salvar o segredo'));
    } finally {
      setBusy(null);
    }
  };

  const handleTest = async (conn: MarketplaceConnection) => {
    try {
      setBusy(conn.id);
      const r = await marketplaceIntegrationService.test(conn.id);
      if (r.data.webhook_delivered === false) toast(r.message, { icon: '⚠️' });
      else toast.success(r.message);
      apply(list.map((c) => (c.id === conn.id ? r.data.connection : c)));
    } catch (e) {
      toast.error(errMsg(e, 'O teste falhou'));
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async (conn: MarketplaceConnection) => {
    if (!window.confirm(`Desconectar a loja "${conn.store_name || conn.store_id}"? Os fluxos param de receber os avisos dela.`)) return;
    try {
      setBusy(conn.id);
      await marketplaceIntegrationService.disconnect(conn.id);
      toast.success('Loja desconectada');
      const rest = list.filter((c) => c.id !== conn.id);
      apply(rest);
      if (selectedId === conn.id) onSelect(rest.length === 1 ? rest[0] : null);
    } catch (e) {
      toast.error(errMsg(e, 'Não consegui desconectar'));
    } finally {
      setBusy(null);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const keyForm = (
    <div className="space-y-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-800 dark:bg-indigo-900/20" data-testid="mkp-key-form">
      <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
        <li>
          No marketplace, abra <strong>Configurações › API Keys</strong> e toque em <strong>Criar chave</strong>.{' '}
          <a href={MARKETPLACE_PANEL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-medium text-indigo-700 underline dark:text-indigo-300">
            Abrir o marketplace <ExternalLink className="h-3 w-3" />
          </a>
        </li>
        <li>Copie a chave (começa com <code className="rounded bg-white px-1 dark:bg-gray-800">dsk_</code>) e cole aqui embaixo.</li>
      </ol>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="dsk_..."
          autoComplete="off"
          className="w-full flex-1 rounded-xl border border-gray-300 bg-white px-3 py-2.5 font-mono text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
        <button
          type="button"
          onClick={handleConnect}
          disabled={connecting || !apiKey.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
          {connecting ? 'Conectando...' : 'Conectar loja'}
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">A gente descobre a loja sozinho e já cadastra os avisos dela aqui.</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando lojas...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mkp-connection-panel">
      {list.length === 0 && keyForm}

      {list.map((conn) => {
        const selected = selectedId === conn.id;
        const needsSecret = conn.status === 'waiting_secret' || (conn.webhook_mode === 'manual' && !conn.has_webhook_secret);
        return (
          <div
            key={conn.id}
            className={`rounded-2xl border-2 p-4 transition-colors ${selected ? 'border-indigo-500 bg-white dark:bg-gray-800' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
                  <Store className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-gray-900 dark:text-white">
                    {conn.status === 'connected' ? `Loja conectada: ${conn.store_name || ''}` : (conn.store_name || 'Loja')}
                  </p>
                  {conn.store_url && (
                    <a href={conn.store_url} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1 truncate text-xs text-gray-500 hover:text-indigo-600">
                      <span className="truncate">{conn.store_url}</span> <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  )}
                </div>
              </div>
              <StatusPill conn={conn} />
            </div>

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <span>Chave: <span className="font-mono">{conn.api_key_masked}</span></span>
              <span>Avisos recebidos: <strong>{conn.events_received || 0}</strong></span>
              {conn.last_event_at && (
                <span>Último: {MARKETPLACE_EVENT_LABELS[conn.last_event_name || ''] || conn.last_event_name} em {fmtDate(conn.last_event_at)}</span>
              )}
              <span>{conn.webhook_mode === 'auto' ? 'Avisos cadastrados sozinhos' : 'Avisos cadastrados à mão'}</span>
            </div>

            {conn.last_error && (
              <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> <span>{conn.last_error}</span>
              </p>
            )}

            {needsSecret && conn.receiver_url && (
              <div className="mt-3 space-y-2 rounded-xl border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20" data-testid="mkp-manual-steps">
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-200">Falta 1 passo: cadastrar o aviso no marketplace</p>
                <ol className="list-decimal space-y-1.5 pl-5 text-sm text-gray-700 dark:text-gray-300">
                  <li>No marketplace, abra <strong>Configurações › Webhooks</strong> e toque em <strong>Novo webhook</strong>.</li>
                  <li>
                    Cole este endereço:
                    <div className="mt-1 flex items-center gap-2">
                      <code className="block flex-1 truncate rounded-lg bg-white px-2 py-1.5 text-xs dark:bg-gray-800">{conn.receiver_url}</code>
                      <button type="button" onClick={() => copy(conn.receiver_url!)} className="rounded-lg bg-yellow-100 p-1.5 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-200" title="Copiar">
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                  <li>Marque os avisos: {(events.length ? events : Object.keys(MARKETPLACE_EVENT_LABELS).filter((e) => e !== 'test')).map((e) => MARKETPLACE_EVENT_LABELS[e] || e).join(', ')}.</li>
                  <li>
                    Salve e copie o <strong>segredo</strong> que aparecer. Cole aqui:
                    <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                      <input
                        type="password"
                        value={secretFor[conn.id] || ''}
                        onChange={(e) => setSecretFor((p) => ({ ...p, [conn.id]: e.target.value }))}
                        placeholder="segredo do webhook"
                        autoComplete="off"
                        className="w-full flex-1 rounded-lg border border-gray-300 bg-white px-2.5 py-2 font-mono text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => handleSecret(conn)}
                        disabled={busy === conn.id || !(secretFor[conn.id] || '').trim()}
                        className="rounded-lg bg-yellow-600 px-3 py-2 text-xs font-semibold text-white hover:bg-yellow-700 disabled:opacity-50"
                      >
                        Salvar segredo
                      </button>
                    </div>
                  </li>
                </ol>
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
              {list.length > 1 && (
                <button
                  type="button"
                  onClick={() => onSelect(conn)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${selected ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300'}`}
                >
                  {selected ? 'Loja deste fluxo' : 'Usar neste fluxo'}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleTest(conn)}
                disabled={busy === conn.id}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:bg-emerald-900/30 dark:text-emerald-300"
              >
                {busy === conn.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Testar
              </button>
              <button
                type="button"
                onClick={() => setShowNewKey(true)}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <KeyRound className="h-3.5 w-3.5" /> Trocar chave
              </button>
              <button
                type="button"
                onClick={() => handleDisconnect(conn)}
                disabled={busy === conn.id}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Unplug className="h-3.5 w-3.5" /> Desconectar
              </button>
            </div>
          </div>
        );
      })}

      {list.length > 0 && (showNewKey ? keyForm : (
        <button type="button" onClick={() => setShowNewKey(true)} className="text-sm font-medium text-indigo-700 hover:underline dark:text-indigo-300">
          + Conectar outra loja (ou trocar a chave)
        </button>
      ))}
    </div>
  );
};

export default MarketplaceConnectionPanel;
